import { ChevronDown, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";

interface TOption {
    id: string;
    label: string;
}

interface TMultiSelectProps {
    options: TOption[];
    selectedIds: string[];
    onChange: (selectedIds: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    /** Selected ids that cannot be removed (no remove button; kept on change). */
    lockedIds?: string[];
    /** Shown inside the open dropdown when there are no selectable options. */
    emptyMessage?: string;
}

export function MultiSelect({
    options,
    selectedIds,
    onChange,
    placeholder = "Select...",
    disabled = false,
    lockedIds = [],
    emptyMessage = "No options available",
}: TMultiSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Reset the query and focus the search box each time the dropdown opens
    useEffect(() => {
        if (isOpen) {
            setSearch("");
            searchRef.current?.focus();
        }
    }, [isOpen]);

    const selectedOptions = options.filter((opt) => selectedIds.includes(opt.id));
    const availableOptions = options.filter((opt) => !selectedIds.includes(opt.id));

    const query = search.trim().toLowerCase();
    const filteredOptions = query
        ? availableOptions.filter((opt) => opt.label.toLowerCase().includes(query))
        : availableOptions;

    const handleSelect = (id: string) => {
        onChange([...selectedIds, id]);
        setIsOpen(false);
    };

    const handleRemove = (id: string) => {
        if (lockedIds.includes(id)) return;
        onChange(selectedIds.filter((selectedId) => selectedId !== id));
    };

    return (
        <div ref={containerRef} className="relative">
            <div
                className={`flex min-h-[42px] w-full items-center justify-between gap-2 px-3 py-2 border rounded-lg bg-background ${
                    disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                } focus-within:outline-none focus-within:ring-2 focus-within:ring-primary`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className="min-w-0 flex-1">
                    {selectedOptions.length === 0 ? (
                        <span className="text-muted-foreground">{placeholder}</span>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {selectedOptions.map((opt) => (
                                <span
                                    key={opt.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm"
                                >
                                    {opt.label}
                                    {!lockedIds.includes(opt.id) && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemove(opt.id);
                                            }}
                                            className="hover:bg-primary/20 rounded-full p-0.5"
                                            disabled={disabled}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>

            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg">
                    {availableOptions.length > 0 ? (
                        <>
                            <div className="p-2 border-b">
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Search..."
                                    className="w-full px-2 py-1 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>
                            <div className="max-h-60 overflow-auto">
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((opt) => (
                                        <div
                                            key={opt.id}
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                            onClick={() => handleSelect(opt.id)}
                                        >
                                            {opt.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-3 py-2 text-sm text-muted-foreground">
                                        No matches found
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                            {emptyMessage}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
